import React, { useEffect, useState } from 'react';
import { connect, MapStateToProps } from 'react-redux';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { BetPrice } from 'app/design-system/components/BetPrice';
import { Icon, IconSize } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import type { RecordOfBetEntity } from 'app/lib/betting/betting-entities';
import { matches } from 'app/modules/Search/selectors';

import dictionary from './Match.dictionary.json';

import styles from './MatchItem.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BettingContextProps = any;

type Props = {
  matchId: number;
  bet: RecordOfBetEntity;
  onBetClick: (matchId?: number, betId?: number) => () => void;
};

type StoreProps = {
  disabled?: boolean;
  isSelectedBet?: boolean;
};

const Bet: React.FC<Props & StoreProps> = ({
  matchId,
  bet,
  onBetClick,
  disabled,
  isSelectedBet,
}) => {
  const { t } = useTranslation();
  const initialDataGa = {
    ['data-ga-event']: isSelectedBet ? 'search-removeFromCart' : 'search-addToCart',
    ['data-ga-event-content']: bet.id,
    ['data-ga-event-value']: bet.price,
    ['data-ga-sport-match']: matchId,
  };
  const [dataGa, setDataGa] = useState<typeof initialDataGa | null>(initialDataGa);

  useEffect(() => {
    setDataGa(initialDataGa);
  }, [isSelectedBet]);

  return (
    <div key={bet.id}
         title={disabled ? t(dictionary.stakeDisabledText) : ''}
         className={classNames(styles.stake, { [styles.disabled]: disabled })}
         data-test={`bet-${bet.key}-${bet.id}`}
         data-test-disabled={disabled}
         data-test-selected={isSelectedBet}>
      <span className={styles.betName}>{bet.names?.get('short')}</span>

      <button
        type="button"
        // title={disabled ? t(dictionary.stakeDisabledText) : bet.names?.get('full')}
        title={disabled ? t(dictionary.stakeDisabledText) : bet.names?.get('long')}
        className={classNames(styles.price, {
          [styles.selectedBet]: isSelectedBet,
        })}
        disabled={disabled}
        {...dataGa}
        onClick={onBetClick(matchId, bet.id)}>
        <BetPrice value={bet.price} />
      </button>

      {disabled && (
        <span className={styles.disabledIcon}>
          <Icon
            icon="lock"
            size={IconSize.SMALL} />
        </span>
      )}
    </div>
  );
};


const mapStateToProps: MapStateToProps<
  StoreProps,
  Props & BettingContextProps,
  ReduxState
> = (state: ReduxState, {
  $betting: {
    staking: {
      selectors: { selectAllConflictedIds, selectBets },
    },
  },
  matchId,
  bet,
}): StoreProps => {
  const match = matches(state).get(matchId);
  const conflictedIds = selectAllConflictedIds(state);

  if (!match) {
    return {};
  }

  const isNotConflictedEntry = conflictedIds.size > 0 && !conflictedIds.has(bet.id);

  const disabled =
    !(match.isOpen && match.stakes?.get(bet.stakeId)?.isOpen)
    || !bet.isOpen
    || isNotConflictedEntry;

  return {
    disabled,
    isSelectedBet: bet && selectBets(state)?.has(bet.id),
  };
};

export default compose<React.FC<Props>>(
  withinBettingContext,
  connect(mapStateToProps),
)(Bet);
